import {useCallback, useEffect, useMemo, useRef} from 'react'

import {TableRowData} from '../types.js'

interface SelectionOptions {
    /** All rows in the order they are shown */
    rows: TableRowData[]
    selectedRows: string[]
    onChange?: (selectedRows: string[]) => void
    loading: boolean
}

/**
 * Selecting rows: one row, a range (shift click), all rows or a page. The selection only holds ids of the current
 * rows: ids of rows that are gone (filtered out) are dropped, and onChange is called with the rest.
 */
export default function useTableSelection({rows, selectedRows, onChange, loading}: SelectionOptions) {
    const selectedSet = useMemo(() => new Set(selectedRows), [selectedRows])
    const rowIds = useMemo(() => rows.map((row) => row._id), [rows])
    const rowIdSet = useMemo(() => new Set(rowIds), [rowIds])

    const selectedCount = useMemo(() => {
        let count = 0

        rowIdSet.forEach((id) => {
            if (selectedSet.has(id)) {
                count++
            }
        })

        return count
    }, [rowIdSet, selectedSet])

    const allSelected = rowIds.length > 0 && selectedCount === rowIds.length
    const someSelected = selectedCount > 0 && !allSelected

    // The callbacks below read the latest values from here, so they never change and rows don't re-render for them
    const latest = useRef({rowIds, rowIdSet, selectedSet, allSelected, onChange})
    latest.current = {rowIds, rowIdSet, selectedSet, allSelected, onChange}

    // The row clicked last, a shift click selects from here
    const anchorRef = useRef<string>(undefined)

    // Drop ids that aren't in rows. Not while loading (rows are about to change), and not twice for the same
    // selection, in case the caller doesn't update selectedRows.
    const prunedRef = useRef<string>(undefined)

    useEffect(() => {
        if (loading || !onChange || !selectedRows.some((id) => !rowIdSet.has(id))) {
            return
        }

        const key = selectedRows.join(',')

        if (prunedRef.current !== key) {
            prunedRef.current = key
            onChange(selectedRows.filter((id) => rowIdSet.has(id)))
        }
    }, [rowIdSet, selectedRows, loading])

    // The selection without ids that aren't in rows
    const currentSelection = () => {
        const {rowIdSet, selectedSet} = latest.current

        return new Set([...selectedSet].filter((id) => rowIdSet.has(id)))
    }

    /** Selects or deselects a row. With shift, the rows from the last clicked row to this one get this row's new state */
    const toggleRow = useCallback((id: string, shiftKey?: boolean) => {
        const {rowIds, selectedSet, onChange} = latest.current

        if (typeof onChange !== 'function') {
            return
        }

        const select = !selectedSet.has(id)
        const anchor = anchorRef.current
        const next = currentSelection()

        anchorRef.current = id

        let range = [id]

        if (shiftKey && anchor !== undefined && anchor !== id) {
            const anchorIndex = rowIds.indexOf(anchor)
            const index = rowIds.indexOf(id)

            if (anchorIndex > -1 && index > -1) {
                range = rowIds.slice(Math.min(anchorIndex, index), Math.max(anchorIndex, index) + 1)
            }
        }

        range.forEach((rangeId) => select ? next.add(rangeId) : next.delete(rangeId))

        onChange([...next])
    }, [])

    /** Selects all rows, or none when all are selected */
    const toggleAll = useCallback(() => {
        const {rowIds, allSelected, onChange} = latest.current

        if (typeof onChange === 'function') {
            onChange(allSelected ? [] : [...rowIds])
        }
    }, [])

    /** Adds these rows (a page) to the selection */
    const addRows = useCallback((ids: string[]) => {
        const {onChange} = latest.current

        if (typeof onChange === 'function') {
            const next = currentSelection()

            ids.forEach((id) => next.add(id))
            onChange([...next])
        }
    }, [])

    return {selectedSet, selectedCount, allSelected, someSelected, toggleRow, toggleAll, addRows}
}
